'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Message } from 'ai';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { getSearchUiContent } from '@/lib/ui-content/search-ui-content';
import { ButtonSuggestion } from './message';
import { DiscussionTurnText } from './discussion-turn-text';
import { useAboveBarMaxHeight } from './use-above-bar-max-height';
import { parseAssistantMessage } from '@/lib/parse-assistant-message';
import type { FocusVisual } from '@/lib/focus-visuals';
import { ComparisonCard } from './comparison-card';
import { LinkAttachmentCard, NoteAttachmentBlock } from './link-attachment-card';
import { UserTextWithLinks } from './user-text-with-links';
import { readMessageAttachments } from '../composer-attachments';

interface DiscussionThreadProps {
  messages: Array<Message>;
  isLoading: boolean;
  locale: LocaleDetails;
  setInput: (input: string) => void;
  /**
   * `true`   — wrap the thread in a soft glass panel (floating above the composer, legacy).
   * `'card'` — the thread IS the top region of the search capsule: no surface of its own,
   *            height clamped by the capsule's `--hero-slot-max` (42vh fallback).
   */
  panel?: boolean | 'card';
  /**
   * The card lives in the phone search sheet (a fixed container on product
   * pages), not on the hero page: it has no hero slot to measure, so its thread
   * gets a slightly taller fixed cap than the 42vh default.
   */
  sheet?: boolean;
  /** Pre-buy seeded conversation: the product being bought, pinned at the top of the thread. */
  pinnedProduct?: { title: string; image?: string };
  /** Assistant-message ids rehydrated by resume — those turns skip the typewriter. */
  restoredTurnIds?: ReadonlySet<string>;
  /**
   * The products the conversation is about, as the catalogue has them (from the
   * focus shelf). A comparison card takes each column's photo, price and link
   * from here; without them it still renders, with neutral tiles.
   */
  focusVisuals?: FocusVisual[];
  /** A click on a compared product (same tracking as a shelf card click). */
  onNavigate?: (url: string) => void;
}

/**
 * Conversational thread for discussion-mode turns: the running history as a minimal
 * chat — assistant turns as plain text, user turns as soft neutral bubbles — with
 * solution-direction chips under the latest assistant turn and a typing indicator
 * while streaming. Product matches render in the live results shelf below the bar.
 */
export function DiscussionThread({
  messages,
  isLoading,
  locale,
  setInput,
  panel = false,
  sheet = false,
  pinnedProduct,
  restoredTurnIds,
  focusVisuals,
  onNavigate,
}: DiscussionThreadProps) {
  const searchContent = getSearchUiContent(locale);
  const [latestTurnStreamDone, setLatestTurnStreamDone] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const latestTurnRef = useRef<HTMLDivElement>(null);
  // Set once the shopper scrolls the panel themselves during a turn; the
  // typewriter then stops moving it under their eyes. Cleared on a new turn.
  const userScrolledRef = useRef(false);

  // Follow the newest text, but never scroll the START of the latest answer
  // out of view: an answer taller than the panel (a comparison table, a full
  // explanation) is read from its top, not from its last line.
  const pinScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || userScrolledRef.current) return;
    const bottom = el.scrollHeight - el.clientHeight;
    const turn = latestTurnRef.current;
    if (!turn) {
      el.scrollTop = bottom;
      return;
    }
    const turnTop = turn.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
    el.scrollTop = Math.max(0, Math.min(bottom, turnTop - 8));
  }, []);

  // Cap the visible history to bound panel height; the scroll pin keeps the newest in view.
  const threadMessages = messages
    .filter((m) => m.id !== 'region-indication' && m.id !== 'region-context')
    .slice(-12);

  const latestAssistant = [...threadMessages]
    .reverse()
    .find((m) => m.role === 'assistant' || m.role === 'system');
  const latestAnswered =
    latestAssistant != null &&
    threadMessages.indexOf(latestAssistant) < threadMessages.length - 1;
  const waitingForReply = isLoading && messages[messages.length - 1]?.role === 'user';

  // Reset chip gate when a new assistant turn starts. A RESTORED turn starts already
  // "done": its instant render fires onStreamComplete from a child effect, which React
  // commits before this parent effect — resetting to false would swallow it.
  useEffect(() => {
    setLatestTurnStreamDone(!!latestAssistant && !!restoredTurnIds?.has(latestAssistant.id));
  }, [latestAssistant?.id, latestAssistant, restoredTurnIds]);

  // A new turn (theirs or ours) hands scrolling back to the thread.
  useEffect(() => {
    userScrolledRef.current = false;
  }, [latestAssistant?.id, messages.length]);

  // Only real input counts as the shopper scrolling — never our own scrollTop writes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const mark = () => {
      userScrolledRef.current = true;
    };
    el.addEventListener('wheel', mark, { passive: true });
    el.addEventListener('touchmove', mark, { passive: true });
    return () => {
      el.removeEventListener('wheel', mark);
      el.removeEventListener('touchmove', mark);
    };
  }, []);

  // Pin the thread to its newest message as turns stream in.
  useEffect(() => {
    pinScroll();
  }, [messages, isLoading, latestTurnStreamDone, pinScroll]);

  // Clamp panel height so it never overlaps the sticky top nav (shared with the floating prompt
  // banner). The panel bottom is anchored at the search bar's top (absolute bottom-full in the parent).
  // The card variant is clamped by the capsule's CSS var instead.
  const panelMaxHeight = useAboveBarMaxHeight(scrollRef, panel === true);
  // Overflow fades, per edge: only an edge with more content past it is
  // masked, so a short thread isn't dimmed and a tall answer read from its top
  // shows at a glance that it continues below.
  const [overflow, setOverflow] = useState<{ above: boolean; below: boolean }>({ above: false, below: false });
  useEffect(() => {
    if (panel !== 'card') return;
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      const above = el.scrollTop > 1;
      const below = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setOverflow((prev) => (prev.above === above && prev.below === below ? prev : { above, below }));
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    // The thread's height changes under it on a phone (the keyboard comes up and
    // the card has to fit the strip above it): keep the latest answer's start in view.
    const ro = new ResizeObserver(() => {
      check();
      pinScroll();
    });
    ro.observe(el);
    const mo = new MutationObserver(check);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
      mo.disconnect();
    };
  }, [panel, pinScroll]);

  // The card panel pins the latest turn's chips BELOW its scroll area, so the
  // next step stays in view while a tall answer (a comparison) scrolls above.
  const chipsInFooter = panel === 'card';
  let footerChips: string[] | null = null;

  const body = (
      <div className="flex w-full flex-col gap-3">
        {pinnedProduct && (
          <div className="flex items-center gap-2 border-b border-zinc-200/70 pb-2 dark:border-white/10">
            {pinnedProduct.image && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={pinnedProduct.image}
                alt=""
                className="h-7 w-7 rounded-md object-contain"
              />
            )}
            <span className="min-w-0 truncate text-xs text-zinc-500 dark:text-zinc-400">
              <span className="mr-1 text-emerald-600 dark:text-emerald-400">✓</span>
              {searchContent.inYourKit ?? 'In your kit:'} {pinnedProduct.title}
            </span>
          </div>
        )}
        {threadMessages.map((msg) => {
          if (msg.role === 'user') {
            let text = msg.content.replace(/^\[User's answer[^\]]*\]:\s*/, '');
            if (text === searchContent.skipInput) text = searchContent.skip;
            // What the user attached to this turn (riding the message): photos
            // as images, links as cards, notes quoted.
            const { images: imageAttachments, links: linkAttachments, notes: noteAttachments } =
              readMessageAttachments(msg);
            return (
              <div key={msg.id} className="ml-auto flex w-fit max-w-[85%] flex-col items-end gap-1.5">
                {imageAttachments.map((att, i) => (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    key={`${msg.id}-attachment-${i}`}
                    src={att.url}
                    alt={att.name ?? searchContent.selectedImage}
                    className="max-h-44 max-w-full rounded-2xl rounded-br-md border border-zinc-200/70 object-cover dark:border-white/10"
                  />
                ))}
                {linkAttachments.map((link) => (
                  <LinkAttachmentCard key={`${msg.id}-link-${link.url}`} url={link.url} host={link.host} locale={locale} />
                ))}
                {noteAttachments.map((note, i) => (
                  <NoteAttachmentBlock key={`${msg.id}-note-${i}`} text={note} locale={locale} />
                ))}
                {text && (
                  <div className="rounded-2xl rounded-br-md bg-zinc-100 px-3.5 py-2 text-sm leading-relaxed text-zinc-700 dark:bg-white/10 dark:text-zinc-200">
                    <UserTextWithLinks text={text} links={linkAttachments} />
                  </div>
                )}
              </div>
            );
          }
          if (msg.role !== 'assistant' && msg.role !== 'system') return null;
          const parsed = parseAssistantMessage({ message: msg });
          const turnText = [parsed.message, parsed.prompt].filter(Boolean).join('\n\n');
          if (!turnText && !parsed.comparison) return null;
          const isLatest = msg.id === latestAssistant?.id;
          const comparison = parsed.comparison ? (
            <ComparisonCard
              comparison={parsed.comparison}
              locale={locale}
              visuals={focusVisuals}
              onNavigate={onNavigate}
            />
          ) : undefined;
          const showChips =
            isLatest &&
            !isLoading &&
            latestTurnStreamDone &&
            !latestAnswered &&
            parsed.suggestedUserAnswers.length > 0;
          return (
            <div
              key={msg.id}
              // Anchor only while this answer is the last thing in the thread:
              // once the shopper replies, the thread follows their reply.
              ref={isLatest && !latestAnswered ? latestTurnRef : undefined}
              className="flex flex-col gap-2.5"
            >
              <div
                className={`${comparison ? 'w-full' : 'w-fit max-w-[92%]'} text-sm leading-relaxed text-gray-800 dark:text-gray-200 [&_.text-base]:text-sm`}
              >
                <DiscussionTurnText
                  message={parsed.message}
                  prompt={parsed.prompt}
                  turnId={msg.id}
                  isLatest={isLatest}
                  instant={!!restoredTurnIds?.has(msg.id)}
                  isLoading={isLoading}
                  onStreamComplete={() => setLatestTurnStreamDone(true)}
                  onProgress={pinScroll}
                  between={comparison}
                />
              </div>
              {(() => {
                if (!showChips) return null;
                const chips = (
                  <div className="flex flex-wrap gap-2">
                    {parsed.suggestedUserAnswers.map((choice) => (
                      <ButtonSuggestion
                        key={choice}
                        choice={choice}
                        isLoading={isLoading}
                        handleSuggestionClick={(c) => setInput(c)}
                      />
                    ))}
                  </div>
                );
                if (!chipsInFooter) return chips;
                footerChips = parsed.suggestedUserAnswers;
                return null;
              })()}
            </div>
          );
        })}

        {waitingForReply && (
          <div className="flex items-center gap-1 px-0.5 py-1.5">
            <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-cornflowerblue/70" style={{ animationDelay: '0ms' }} />
            <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-cornflowerblue/70" style={{ animationDelay: '150ms' }} />
            <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-cornflowerblue/70" style={{ animationDelay: '300ms' }} />
          </div>
        )}
      </div>
    );

  if (!panel) {
    return <div className="max-h-[45vh] overflow-y-auto overscroll-contain" ref={scrollRef}>{body}</div>;
  }

  if (panel === 'card') {
    const mask = `linear-gradient(to bottom, ${overflow.above ? 'transparent, black 1.25rem' : 'black, black'}, ${
      overflow.below ? 'black calc(100% - 2rem), transparent' : 'black'
    })`;
    return (
      // The clamp covers the answer AND the pinned chips: the capsule measures
      // this whole region against the hero slot (--hero-slot-max).
      // On a phone's hero composer the capsule sets --thread-h instead: the thread
      // is exactly as tall as the free screen (see phone-thread-fit.ts), full even
      // while the answer is short, so the composer sits at the bottom.
      <div
        className="flex flex-col"
        style={{
          height: 'var(--thread-h, auto)',
          maxHeight: `var(--thread-h, min(${sheet ? 50 : 42}vh, var(--hero-slot-max, ${sheet ? 50 : 42}vh)))`,
        }}
      >
        <div
          ref={scrollRef}
          style={{
            overflowAnchor: 'none',
            ...(overflow.above || overflow.below ? { maskImage: mask, WebkitMaskImage: mask } : null),
          }}
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain px-3.5 pt-4 sm:px-4 sm:pt-5 ${
            footerChips ? 'pb-2' : 'pb-3 sm:pb-3.5'
          }`}
        >
          {body}
        </div>
        {footerChips && (
          <ChipsRail choices={footerChips} isLoading={isLoading} onChoose={(c) => setInput(c)} />
        )}
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      style={panelMaxHeight != null ? { maxHeight: `${panelMaxHeight}px` } : undefined}
      className="max-h-[42vh] overflow-y-auto overscroll-contain rounded-2xl p-3.5 backdrop-blur-md sm:p-4"
    >
      {body}
    </div>
  );
}

/**
 * The latest turn's answer chips as ONE horizontal row pinned under the
 * thread. Wrapping stacked four long chips one per line on a phone and left
 * the answer a sliver; a rail keeps the footer one chip tall. The right edge
 * fades only while more chips sit past it.
 */
function ChipsRail({
  choices,
  isLoading,
  onChoose,
}: {
  choices: string[];
  isLoading: boolean;
  onChoose: (choice: string) => void;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const check = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [choices.length]);
  const fade = more ? 'linear-gradient(to right, black calc(100% - 2rem), transparent)' : undefined;
  return (
    <div className="flex-none pb-3 pt-1 sm:pb-3.5">
      <div
        ref={railRef}
        style={fade ? { maskImage: fade, WebkitMaskImage: fade } : undefined}
        className="no-scrollbar flex flex-nowrap gap-2 overflow-x-auto overscroll-x-contain px-3.5 pb-0.5 sm:px-4 [&>*]:shrink-0 [&_button]:whitespace-nowrap"
      >
        {choices.map((choice) => (
          <ButtonSuggestion key={choice} choice={choice} isLoading={isLoading} handleSuggestionClick={onChoose} />
        ))}
      </div>
    </div>
  );
}
